import React from 'react';
import {
  Box,
  Stack,
  Flex,
  Text,
  Button,
  Badge,
  Heading,
  Divider,
  useColorModeValue,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSetAtom, useAtomValue } from 'jotai';
import { 
  cancelSubscriptionAtom, 
  createCheckoutSessionAtom, 
  subscriptionPricesAtom,
  getPriceByPlanAndIntervalAtom,
  resumeSubscriptionAtom
} from '../../../atoms/v0/subscriptionAtoms';
import { SUBSCRIPTION_PLAN, SUBSCRIPTION_INTERVAL, SUBSCRIPTION_STATUS } from '../../../constants/subscription';

interface SubscriptionManagementProps {
  subscription: any;
  isLoading?: boolean;
}

/**
 * 订阅管理组件
 * 显示当前订阅信息并提供管理功能（如取消订阅）
 */
const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({
  subscription,
  isLoading = false,
}) => {
  const { t } = useTranslation(['v0/subscription', 'v0/common']);
  const cancelSubscription = useSetAtom(cancelSubscriptionAtom);
  const resumeSubscription = useSetAtom(resumeSubscriptionAtom);
  const createCheckoutSession = useSetAtom(createCheckoutSessionAtom);
  const subscriptionPrices = useAtomValue(subscriptionPricesAtom);
  const getPriceByPlanAndInterval = useAtomValue(getPriceByPlanAndIntervalAtom);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const cardBg = useColorModeValue('#FFFFFF', '#1C1C1E');
  const borderColor = useColorModeValue('#EBEBEB', '#2C2C2E');
  const textColor = useColorModeValue('#767676', '#A0A0A0');
  const dangerColor = useColorModeValue('#FF5A5F', '#FF8C8D');
  
  // 没有订阅数据
  if (!subscription) {
    return (
      <Box 
        p={{ base: 4, md: 6 }} 
        borderWidth="1px" 
        borderRadius="lg" 
        bg={cardBg} 
        borderColor={borderColor}
        shadow="sm"
      >
        <Text fontSize={{ base: 'sm', md: 'md' }} color={textColor}>
          {t('v0/subscription:noActiveSubscription')}
        </Text>
      </Box>
    );
  }
  
  // 获取订阅状态显示
  const getStatusDisplay = () => {
    // 如果订阅处于活跃状态但已设置为周期结束时取消，显示特殊状态
    if (subscription.status === SUBSCRIPTION_STATUS.ACTIVE && subscription.cancelAtPeriodEnd) {
      return { label: t('v0/subscription:status.scheduledCancel'), color: 'orange' };
    }

    const statusMap = {
      [SUBSCRIPTION_STATUS.ACTIVE]: { label: t('v0/subscription:status.active'), color: 'green' },
      [SUBSCRIPTION_STATUS.TRIALING]: { label: t('v0/subscription:status.trialing'), color: 'blue' },
      [SUBSCRIPTION_STATUS.CANCELED]: { label: t('v0/subscription:status.canceled'), color: 'red' },
      [SUBSCRIPTION_STATUS.INCOMPLETE]: { label: t('v0/subscription:status.incomplete'), color: 'yellow' },
      [SUBSCRIPTION_STATUS.INCOMPLETE_EXPIRED]: { label: t('v0/subscription:status.incompleteExpired'), color: 'red' },
      [SUBSCRIPTION_STATUS.PAST_DUE]: { label: t('v0/subscription:status.pastDue'), color: 'orange' },
      [SUBSCRIPTION_STATUS.UNPAID]: { label: t('v0/subscription:status.unpaid'), color: 'red' },
    };
    
    return statusMap[subscription.status] || { label: subscription.status, color: 'gray' };
  };
  
  // 格式化日期
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'yyyy-MM-dd');
    } catch (e) {
      return dateString;
    }
  };
  
  // 处理取消订阅
  const handleCancelSubscription = async () => {
    try {
      await cancelSubscription(subscription.stripeSubscriptionId);
      onClose();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    }
  };
  
  // 处理恢复订阅
  const handleResumeSubscription = async () => {
    try {
      await resumeSubscription(subscription.stripeSubscriptionId);
    } catch (error) {
      console.error('Failed to resume subscription:', error);
    }
  };
  
  // 处理继续支付
  const handleContinuePayment = async () => {
    try {
      if (subscription.latestInvoice && subscription.latestInvoice.payment_intent) {
        // 如果有未完成的payment_intent，使用它继续支付流程
        window.location.href = subscription.latestInvoice.payment_intent.next_action.redirect_to_url.url;
      } else {
        // 从已有数据中获取价格信息
        // 首先尝试从订阅中获取
        let priceId = null;
        
        // 如果无法从订阅中获取价格ID，则尝试从价格列表获取默认价格ID
        if (!priceId && subscriptionPrices) {
          const defaultPrice = getPriceByPlanAndInterval(SUBSCRIPTION_PLAN.PLUS, SUBSCRIPTION_INTERVAL.MONTH);
          if (defaultPrice) {
            priceId = defaultPrice.id;
          }
        }
        
        // 如果仍然没有价格ID，则使用订阅ID创建特殊会话
        if (!priceId && subscription.stripe_subscription_id) {
          await createCheckoutSession(`recover_${subscription.stripe_subscription_id}`);
          return;
        }
        
        if (priceId) {
          await createCheckoutSession(priceId);
        } else {
          console.error('无法获取订阅价格信息，无法继续支付');
          alert(t('v0/subscription:errors.continuePaymentFailed'));
        }
      }
    } catch (error) {
      console.error('Failed to continue payment:', error);
    }
  };
  
  const { label: statusLabel, color: statusColor } = getStatusDisplay();
  const isIncomplete = subscription.status === SUBSCRIPTION_STATUS.INCOMPLETE;
  
  return (
    <Box 
      p={{ base: 5, md: 6 }} 
      borderWidth="1px" 
      borderRadius="lg" 
      bg={cardBg} 
      borderColor={borderColor} 
      width="100%"
      shadow="sm"
    >
      <Stack spacing={4} width="100%">
        <Flex 
          width="100%" 
          justifyContent="space-between" 
          alignItems={{ base: 'flex-start', sm: 'center' }}
          flexDir={{ base: 'column', sm: 'row' }}
          gap={{ base: 2, sm: 0 }}
        >
          <Heading 
            fontSize={{ base: 'lg', md: 'xl' }}
            fontWeight="500"
          >
            {t('v0/subscription:management.currentPlan')}
          </Heading>
          <Badge 
            colorScheme={statusColor} 
            fontSize={{ base: 'xs', md: 'sm' }}
            px={2}
            py={0.5}
            borderRadius="full"
          >
            {statusLabel}
          </Badge>
        </Flex>
        
        <Divider />
        
        {/* 订阅信息卡片 */}
        <Stack spacing={3} width="100%" fontSize={{ base: 'sm', md: 'md' }}>
          <Flex justifyContent="space-between">
            <Text fontWeight="500" color={textColor}>
              {t('v0/subscription:management.plan')}
            </Text>
            <Text fontWeight="400">
              {subscription.name || 'Plus'}
            </Text>
          </Flex>
          
          <Flex justifyContent="space-between">
            <Text fontWeight="500" color={textColor}>
              {t('v0/subscription:management.billingPeriod')}
            </Text>
            <Text fontWeight="400">
              {subscription.interval === 'year' 
                ? t('v0/subscription:billing.yearly') 
                : t('v0/subscription:billing.monthly')}
            </Text>
          </Flex>
          
          <Flex justifyContent="space-between">
            <Text fontWeight="500" color={textColor}>
              {t('v0/subscription:management.currentPeriodEnd')}
            </Text>
            <Text fontWeight="400">
              {formatDate(subscription.currentPeriodEnd)}
            </Text>
          </Flex>
          
          {subscription.cancelAtPeriodEnd && (
            <Flex justifyContent="space-between">
              <Text fontWeight="500" color={textColor}>
                {t('v0/subscription:management.cancelsAt')}
              </Text>
              <Text fontWeight="medium" color={dangerColor}>
                {formatDate(subscription.currentPeriodEnd)}
              </Text>
            </Flex>
          )}
        </Stack>
        
        <Divider />
        
        {/* 操作按钮区域 */}
        <Box width="100%" pt={2}>
          {subscription.cancelAtPeriodEnd ? (
            <Stack spacing={4}>
              <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                {t('v0/subscription:management.canceledMessage')}
              </Text>
              <Button 
                bg="#00A699"
                _hover={{ bg: "#008F86" }}
                color="white"
                size={{ base: 'md', md: 'lg' }}
                onClick={handleResumeSubscription}
                isLoading={isLoading}
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.resumeSubscription')}
              </Button>
            </Stack>
          ) : subscription.status === SUBSCRIPTION_STATUS.CANCELED && new Date(subscription.currentPeriodEnd) > new Date() ? (
            <Stack spacing={4}>
              <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                {t('v0/subscription:management.canceledMessage')}
              </Text>
              <Button 
                bg="#00A699"
                _hover={{ bg: "#008F86" }}
                color="white"
                size={{ base: 'md', md: 'lg' }}
                onClick={handleContinuePayment}
                isLoading={isLoading}
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.resumeSubscription')}
              </Button>
            </Stack>
          ) : isIncomplete ? (
            <Stack spacing={4}>
              <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                {t('v0/subscription:management.incompleteInfo')}
              </Text>
              <Button 
                bg="#00A699"
                _hover={{ bg: "#008F86" }}
                color="white"
                size={{ base: 'md', md: 'lg' }}
                onClick={handleContinuePayment}
                isLoading={isLoading}
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.continuePayment')}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={4}>
              <Text color={textColor} fontSize={{ base: 'sm', md: 'md' }}>
                {t('v0/subscription:management.cancelInfo')}
              </Text>
              <Button 
                variant="outline"
                borderColor={dangerColor}
                color={dangerColor}
                _hover={{ bg: useColorModeValue('red.50', 'rgba(255,90,95,0.1)') }}
                size={{ base: 'md', md: 'lg' }}
                onClick={onOpen}
                isLoading={isLoading}
                isDisabled={['canceled', 'incomplete_expired', 'unpaid'].includes(subscription.status)}
                h={{ base: '44px', md: '50px' }}
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="500"
              >
                {t('v0/subscription:actions.cancelSubscription')}
              </Button>
            </Stack>
          )}
        </Box>
      </Stack>
      
      {/* 取消订阅确认对话框 */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent
            mx={4}
            borderRadius="lg"
            bg={cardBg}
          >
            <AlertDialogHeader 
              fontSize={{ base: 'lg', md: 'xl' }} 
              fontWeight="500"
              pb={2}
            >
              {t('v0/subscription:cancel.title')}
            </AlertDialogHeader>

            <AlertDialogBody py={4} fontSize={{ base: 'sm', md: 'md' }}>
              {t('v0/subscription:cancel.message')}
            </AlertDialogBody>

            <AlertDialogFooter gap={3}>
              <Button 
                ref={cancelRef} 
                onClick={onClose}
                variant="outline"
                fontSize={{ base: 'sm', md: 'md' }}
                h={{ base: '40px', md: '44px' }}
                fontWeight="500"
              >
                {t('v0/common:actions.cancel')}
              </Button>
              <Button 
                bg={dangerColor}
                _hover={{ bg: '#E34850' }}
                color="white"
                onClick={handleCancelSubscription} 
                isLoading={isLoading}
                fontSize={{ base: 'sm', md: 'md' }}
                h={{ base: '40px', md: '44px' }}
                fontWeight="500"
              >
                {t('v0/subscription:cancel.confirm')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default SubscriptionManagement; 